/**
 * D-1028-ONELANG-8 — "Spanish wording (for N people)": the box the office types the Spanish of its own free text into
 * (an event description, what to bring, a workshop note, a message). It sits next to the English one and is shown only
 * when there are people marked Spanish to send to. The app never translates; if it is left blank, those people get the
 * whole email in English.
 */
import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '@/lib/api';

/** How many people have Spanish on their own record (tenants and volunteers who can be emailed). null = not known (yet). */
export function useSpanishPeople(): number | null {
  const q = useQuery({
    queryKey: ['spanish-people'],
    queryFn: () => apiFetch<{ spanishMarked: number }>('/api/announcements/audience', { method: 'POST', body: JSON.stringify({ audience: { kind: 'both' }, spanish: false }) }),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const n = q.data?.spanishMarked;
  return typeof n === 'number' ? n : null;
}

export interface SpanishWordingProps {
  /** The English label of the field this is the Spanish of (for the screen reader: "Spanish wording of …"). */
  of?: string;
  /** People marked Spanish; the box shows when this is above 0, or when words are already kept (never hide them). */
  count: number | null;
  value: string;
  onChange: (v: string) => void;
  /** One line or a few. */
  rows?: number;
  maxLength?: number;
  testId?: string;
  id?: string;
}

export function SpanishWording({ of, count, value, onChange, rows = 1, maxLength = 300, testId, id }: SpanishWordingProps) {
  const { t } = useTranslation();
  const auto = useId();
  const boxId = id ?? auto;
  if (!(count && count > 0) && !value.trim()) return null;
  const n = count ?? 0;
  return (
    <div className="field" data-testid={testId ? `${testId}-field` : undefined}>
      <label htmlFor={boxId}>{n > 0 ? t('oneLang.boxLabel', { count: n }) : t('oneLang.boxLabelKept')}</label>
      {rows > 1 ? (
        <textarea id={boxId} className="input" lang="es" rows={rows} maxLength={maxLength} value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={`${boxId}-help`} data-testid={testId} />
      ) : (
        <input id={boxId} className="input" lang="es" maxLength={maxLength} value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={`${boxId}-help`} data-testid={testId} />
      )}
      <span className="help" id={`${boxId}-help`}>
        {of ? t('oneLang.boxHelpOf', { of }) : t('oneLang.boxHelp')}
      </span>
    </div>
  );
}
